import { Button, ModalSection } from '@monash/portal-react';
import cs from '../bulk-actions-shared.module.scss';
import { useForm } from 'react-hook-form';
import ControlledLinkDetailsList from '../controlled-link-details-list/ControlledLinkDetailsList';
import { useWatchCheckedLinksCount } from '../hooks';
import { isPublishable } from '../../utils/statuses';
import { useContext, useState } from 'react';
import { useAllLinkIdsMap } from '../../hooks/use-all-link-ids-map';
import { APIContext } from '@monash/portal-frontend-common';
import { useSnackbar } from '../../../../providers/SnackbarProvider';
import { useLinksState } from '../../hooks/use-links-state';

const BulkPublishModalContents = ({
  links,
  onClose,
  updateLink: updateLinkInParent,
  updateChecked,
  ...modalSectionProps
}) => {
  const { updateLink } = useContext(APIContext);
  const { addSnackbar } = useSnackbar();

  // Use a local links state for the modal so that links persist
  // regardless of status changes or top level filters
  const { links: modalLinks, updateLink: updateLinkInModal } = useLinksState(
    () => links.filter(isPublishable)
  );

  const [hasAttemptedPublish, setHasAttemptedPublish] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState({});
  const isSomeLinkLoading = Object.values(isLoadingMap).some(Boolean);
  const [errorMap, setErrorMap] = useState({});
  const [disabledMap, setDisabledMap] = useState({});

  const enrichedModalLinks = modalLinks.map((link) => {
    return {
      ...link,
      isLoading: isLoadingMap[link.id],
      error: errorMap[link.id],
      disabled: disabledMap[link.id],
    };
  });

  const allIdsChecked = useAllLinkIdsMap({
    links: enrichedModalLinks,
    value: true,
  });

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      checkedLinkIds: allIdsChecked,
    },
  });

  const { control, handleSubmit } = methods;

  const checkedLinksCount = useWatchCheckedLinksCount({
    name: 'checkedLinkIds',
    control,
  });

  const attemptPublish = async (link) => {
    const { id } = link;

    setIsLoadingMap((prevMap) => {
      return {
        ...prevMap,
        [id]: true,
      };
    });

    try {
      const updatedLink = await updateLink(link, true);
      updateLinkInParent(updatedLink);
      updateLinkInModal(updatedLink);
      setDisabledMap((prevMap) => {
        return {
          ...prevMap,
          [id]: true,
        };
      });
    } catch (error) {
      setErrorMap((prevMap) => {
        return {
          ...prevMap,
          [id]: 'Failed to publish',
        };
      });

      const errorObj = new Error(error);
      errorObj.id = id;
      throw errorObj; // handle when all promises resolved
    } finally {
      setIsLoadingMap((prevMap) => {
        return {
          ...prevMap,
          [id]: false,
        };
      });
    }
  };

  const submitHandler = async (data) => {
    const { checkedLinkIds } = data;
    const checkedLinks = enrichedModalLinks.filter((link) => {
      const id = link.id;
      return checkedLinkIds[id];
    });

    const requests = checkedLinks.map((link) => {
      return attemptPublish(link);
    });

    setHasAttemptedPublish(true);
    const results = await Promise.allSettled(requests);

    const failedLinkIds = results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason.id);

    const failedLinkIdsMap = Object.fromEntries(
      failedLinkIds.map((id) => [id, true])
    );

    updateChecked(failedLinkIdsMap);

    const numberFailed = failedLinkIds.length;

    if (numberFailed > 0) {
      addSnackbar({
        message: `${numberFailed} out of ${results.length} links failed`,
        type: 'error',
      });
    }
  };

  return (
    <ModalSection
      title={`Publish ${checkedLinksCount} link${
        checkedLinksCount === 1 ? '' : 's'
      }`}
      noPadding
      onClose={onClose}
      {...modalSectionProps}
    >
      <form onSubmit={handleSubmit(submitHandler)}>
        <ControlledLinkDetailsList
          links={enrichedModalLinks}
          name="checkedLinkIds"
          control={control}
        />
        <div className={cs.actionInput}>
          {hasAttemptedPublish ? (
            <Button
              variant="primary"
              size="medium"
              onClick={onClose}
              loading={isSomeLinkLoading}
            >
              Done
            </Button>
          ) : (
            <Button
              variant="primary"
              size="medium"
              type="submit"
              loading={isSomeLinkLoading}
              disabled={checkedLinksCount === 0}
            >
              Publish
            </Button>
          )}
        </div>
      </form>
    </ModalSection>
  );
};

export default BulkPublishModalContents;
